import { API_BASE_URL } from "../../../config";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CustomerLink from '../../Components/CustomerLink';

const getToken = () => {
    const user = localStorage.getItem("user");
    if (!user) return null;
    try { return JSON.parse(user)?.token || null; } catch { return null; }
};

const useAuthHeaders = () => {
    const token = getToken();
    return useMemo(() => token ? ({
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
    }) : ({ Accept: "application/json" }), [token]);
};

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// Work Week starts Monday
const startOfWeekMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay(); // 0=Sun..6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    return date;
};
const endOfWeek = (monday) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + 6);
    return d;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const formatThaiDate = (d) => d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });

// Tank count at shop per customer comes from one batch call (GET /customer/api/tanks/shop-count-by-customer),
// never per-row. Customers absent from the map have zero tanks at shop — that is 0, not blank.
export const tankCountOf = (counts, customerId) => (counts ? (counts[customerId] || 0) : null);

// Brand palette only (white / black / gray / red-as-accent) — top-tier (S/A) customers
// are the ones worth prioritizing for promotions, so they alone get the red accent;
// everyone else reads as neutral gray. Never a rainbow of grades.
const isTopTier = (cls) => !!cls && /^(S|A)/.test(cls);
const gradeBadgeStyle = (cls) => (isTopTier(cls)
    ? { background: '#E60012', color: '#fff' }
    : { background: '#E5E5E5', color: '#000' });

// Horizontal bar-as-list ranking chart. Single series (magnitude only) — length carries
// the value, the red accent is reserved for the subset that matters (top-tier grade).
const RankingBarChart = ({ data }) => {
    const max = Math.max(1, ...data.map(d => d.totalQuantity || 0));
    return (
        <div>
            {data.map((c, i) => {
                const pct = Math.max(3, Math.round(((c.totalQuantity || 0) / max) * 100));
                const top = isTopTier(c.customerClass);
                return (
                    <div key={c.customerId} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <div style={{
                            width: 160, fontSize: 12, color: '#000', textAlign: 'right',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }} title={c.customerName}>
                            {i + 1}. <CustomerLink name={c.customerName} code={c.customerId} />
                        </div>
                        <div style={{ flex: 1, background: '#F7F7F7', borderRadius: 4, height: 18 }}>
                            <div
                                title={`${c.customerName}: ${c.totalQuantity} ถุง (เกรด ${c.customerClass || '-'})`}
                                style={{
                                    width: `${pct}%`, height: '100%', minWidth: 4,
                                    background: top ? '#E60012' : '#495057',
                                    borderRadius: 4,
                                }}
                            />
                        </div>
                        <div style={{ width: 46, fontSize: 12, color: '#6B6B6B', fontWeight: 600, textAlign: 'right' }}>
                            {c.totalQuantity}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Per-customer daily column chart. Zero-purchase days render as a thin flat sliver
// (never absent) so the buying pattern ("which days") reads at a glance.
const DailyBarChart = ({ days }) => {
    const max = Math.max(1, ...days.map(d => d.quantity || 0));
    const sparse = days.length > 10;
    const labelEvery = sparse ? Math.ceil(days.length / 10) : 1;
    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 110, borderBottom: '1px solid #E5E5E5' }}>
                {days.map(d => {
                    const hasData = (d.quantity || 0) > 0;
                    const heightPct = hasData ? Math.max(8, Math.round((d.quantity / max) * 100)) : 7;
                    return (
                        <div key={d.date} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                            <div
                                title={`${d.date} (${d.dayOfWeek}): ${d.quantity} ถุง, ${Number(d.total).toLocaleString()} ฿`}
                                style={{
                                    width: '100%', maxWidth: 22, margin: '0 auto',
                                    height: `${heightPct}%`,
                                    // Zero days get a visibly-distinct light-gray "track" (a step darker than
                                    // the divider gray) so the pattern reads at a glance, not just a table lookup.
                                    background: hasData ? '#495057' : '#D9D9D9',
                                    borderRadius: '3px 3px 0 0',
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
                {days.map((d, i) => (
                    <div key={d.date} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: '#6B6B6B' }}>
                        {i % labelEvery === 0 ? new Date(d.date + 'T00:00:00').getDate() : ''}
                    </div>
                ))}
            </div>
        </div>
    );
};

const CustomerPurchaseBehavior = () => {
    const headers = useAuthHeaders();

    const [mode, setMode] = useState('weekly'); // 'weekly' | 'monthly'
    const [anchorDate, setAnchorDate] = useState(new Date());
    const [customRange, setCustomRange] = useState(null); // { start: Date, end: Date } | null
    const [customStartInput, setCustomStartInput] = useState('');
    const [customEndInput, setCustomEndInput] = useState('');

    const [ranking, setRanking] = useState([]);
    const [loadingRanking, setLoadingRanking] = useState(false);
    const [rankingError, setRankingError] = useState('');

    const [tankCounts, setTankCounts] = useState(null); // { [customerUid]: n } | null while not loaded / failed
    const [tankCountError, setTankCountError] = useState('');

    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [behavior, setBehavior] = useState(null);
    const [loadingBehavior, setLoadingBehavior] = useState(false);

    const effectiveRange = useMemo(() => {
        if (customRange) return customRange;
        if (mode === 'weekly') {
            const start = startOfWeekMonday(anchorDate);
            return { start, end: endOfWeek(start) };
        }
        const start = startOfMonth(anchorDate);
        return { start, end: endOfMonth(anchorDate) };
    }, [mode, anchorDate, customRange]);

    const rangeLabel = useMemo(() => {
        if (customRange) return `ช่วงที่เลือก: ${formatThaiDate(customRange.start)} - ${formatThaiDate(customRange.end)}`;
        if (mode === 'weekly') return `สัปดาห์: ${formatThaiDate(effectiveRange.start)} - ${formatThaiDate(effectiveRange.end)}`;
        return `เดือน: ${anchorDate.toLocaleDateString('th-TH', { year: 'numeric', month: 'long' })}`;
    }, [mode, anchorDate, effectiveRange, customRange]);

    const goPrev = () => {
        setCustomRange(null);
        setAnchorDate(prev => {
            const d = new Date(prev);
            if (mode === 'weekly') d.setDate(d.getDate() - 7);
            else d.setMonth(d.getMonth() - 1);
            return d;
        });
    };
    const goNext = () => {
        setCustomRange(null);
        setAnchorDate(prev => {
            const d = new Date(prev);
            if (mode === 'weekly') d.setDate(d.getDate() + 7);
            else d.setMonth(d.getMonth() + 1);
            return d;
        });
    };
    const applyCustomRange = () => {
        if (!customStartInput || !customEndInput) return;
        const start = new Date(customStartInput + 'T00:00:00');
        const end = new Date(customEndInput + 'T00:00:00');
        if (end < start) return;
        setCustomRange({ start, end });
    };
    const clearCustomRange = () => {
        setCustomRange(null);
        setCustomStartInput('');
        setCustomEndInput('');
    };

    // Tank count is a current snapshot (not date-ranged), so it loads once on mount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetch(`${API_BASE_URL}/customer/api/tanks/shop-count-by-customer`, { headers });
                if (!resp.ok) throw new Error(`โหลดจำนวนถังไม่สำเร็จ (${resp.status})`);
                const data = await resp.json();
                if (!cancelled) setTankCounts(data || {});
            } catch (e) {
                if (!cancelled) setTankCountError(e.message || 'เกิดข้อผิดพลาด');
            }
        })();
        return () => { cancelled = true; };
    }, [headers]);

    const fetchRanking = useCallback(async (start, end) => {
        setLoadingRanking(true);
        setRankingError('');
        try {
            const url = `${API_BASE_URL}/saledata/api/module/customers/purchase-ranking?startDate=${toISO(start)}&endDate=${toISO(end)}`;
            const resp = await fetch(url, { headers });
            if (!resp.ok) throw new Error(`โหลดอันดับลูกค้าไม่สำเร็จ (${resp.status})`);
            const data = await resp.json();
            setRanking(Array.isArray(data?.customers) ? data.customers : []);
        } catch (e) {
            setRankingError(e.message || 'เกิดข้อผิดพลาด');
            setRanking([]);
        } finally {
            setLoadingRanking(false);
        }
    }, [headers]);

    useEffect(() => {
        fetchRanking(effectiveRange.start, effectiveRange.end);
        setSelectedCustomerId(null);
        setBehavior(null);
    }, [effectiveRange.start, effectiveRange.end, fetchRanking]); // eslint-disable-line react-hooks/exhaustive-deps

    const openDetail = async (customerId) => {
        setSelectedCustomerId(customerId);
        setLoadingBehavior(true);
        setBehavior(null);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/customers/${encodeURIComponent(customerId)}/purchase-behavior?startDate=${toISO(effectiveRange.start)}&endDate=${toISO(effectiveRange.end)}`;
            const resp = await fetch(url, { headers });
            if (resp.ok) {
                const data = await resp.json();
                setBehavior(data);
                // Patch just this one row with the freshly-fetched numbers instead of
                // re-fetching the whole ranking (144+ customers + 2 cross-service calls) —
                // that full refetch caused a real hang (vector caught it 2026-07-06). This
                // keeps the two totals in sync at zero extra network cost.
                setRanking(prev => prev.map(c => (c.customerId === customerId
                    ? { ...c, totalQuantity: data.totalQuantity, distinctPurchaseDays: data.distinctPurchaseDays }
                    : c)));
            }
        } catch (e) { /* silent */ }
        finally { setLoadingBehavior(false); }
    };

    const closeDetail = () => {
        setSelectedCustomerId(null);
        setBehavior(null);
    };

    return (
        <div className="btc-container">
            <h1>พฤติกรรมการซื้อลูกค้า</h1>

            <div className="btc-card" style={{ padding: '16px 24px', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                    <button
                        className="btc-btn btn-sm"
                        style={{ backgroundColor: mode === 'weekly' && !customRange ? '#E60012' : '#6B6B6B' }}
                        onClick={() => { setCustomRange(null); setMode('weekly'); }}
                    >
                        รายสัปดาห์
                    </button>
                    <button
                        className="btc-btn btn-sm"
                        style={{ backgroundColor: mode === 'monthly' && !customRange ? '#E60012' : '#6B6B6B' }}
                        onClick={() => { setCustomRange(null); setMode('monthly'); }}
                    >
                        รายเดือน
                    </button>
                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={goPrev} disabled={!!customRange}>
                        ← ก่อนหน้า
                    </button>
                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={goNext} disabled={!!customRange}>
                        ถัดไป →
                    </button>
                    <span style={{ fontWeight: 600, color: '#343a40', marginLeft: 8 }}>{rangeLabel}</span>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #E5E5E5', paddingTop: 12 }}>
                    <span style={{ fontSize: 13, color: '#6B6B6B' }}>เลือกช่วงเอง:</span>
                    <input type="date" value={customStartInput} onChange={e => setCustomStartInput(e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #E5E5E5' }} />
                    <span>-</span>
                    <input type="date" value={customEndInput} onChange={e => setCustomEndInput(e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #E5E5E5' }} />
                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={applyCustomRange}>ใช้ช่วงนี้</button>
                    {customRange && (
                        <button className="btc-btn btn-sm" style={{ backgroundColor: '#6B6B6B' }} onClick={clearCustomRange}>ล้าง</button>
                    )}
                </div>
            </div>

            {!loadingRanking && ranking.length > 0 && (
                <div className="btc-card" style={{ padding: '16px 24px', marginBottom: 16 }}>
                    <h3 style={{ marginTop: 0 }}>
                        กราฟอันดับลูกค้า
                        {ranking.length > 15 && (
                            <span style={{ fontSize: 12, fontWeight: 400, color: '#6B6B6B', marginLeft: 8 }}>
                                (แสดง 15 อันดับแรก จากทั้งหมด {ranking.length} ราย — ดูรายชื่อทั้งหมดในตารางด้านล่าง)
                            </span>
                        )}
                    </h3>
                    <RankingBarChart data={ranking.slice(0, 15)} />
                </div>
            )}

            <div className="btc-card" style={{ padding: '16px 24px' }}>
                <h3 style={{ marginTop: 0 }}>อันดับลูกค้า (ลูกค้าประจำ) — {ranking.length} ราย</h3>
                {loadingRanking && <p>กำลังโหลด...</p>}
                {rankingError && <p className="error">{rankingError}</p>}
                {tankCountError && <p className="error">คอลัมน์ถังที่ร้าน: {tankCountError}</p>}
                {!loadingRanking && !rankingError && ranking.length === 0 && <p>ไม่มีข้อมูลในช่วงนี้</p>}
                {!loadingRanking && ranking.length > 0 && (
                    <table className="btc-table" style={{ width: '100%', fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 36 }}>#</th>
                                <th>ชื่อร้าน</th>
                                <th style={{ width: 60 }}>เกรด</th>
                                <th style={{ width: 90 }}>ถุงรวม</th>
                                <th style={{ width: 90 }}>วันที่ซื้อ</th>
                                <th style={{ width: 100 }}>ถุง/สัปดาห์</th>
                                <th style={{ width: 90 }}>ถังที่ร้าน</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranking.map((c, i) => (
                                <tr
                                    key={c.customerId}
                                    onClick={() => openDetail(c.customerId)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{i + 1}</td>
                                    <td><CustomerLink name={c.customerName} code={c.customerId} onClick={(e) => e.stopPropagation()} /></td>
                                    <td>
                                        <span style={{ ...gradeBadgeStyle(c.customerClass), borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                                            {c.customerClass || '-'}
                                        </span>
                                    </td>
                                    <td>{c.totalQuantity}</td>
                                    <td>{c.distinctPurchaseDays}</td>
                                    <td>{c.bagsPerWeek}</td>
                                    <td>{tankCountOf(tankCounts, c.customerId) ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {selectedCustomerId && (
                <div
                    onClick={closeDetail}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                        padding: '40px 16px', overflowY: 'auto',
                    }}
                >
                    <div
                        className="btc-card"
                        onClick={e => e.stopPropagation()}
                        style={{ padding: '20px 24px', width: '100%', maxWidth: 640, background: '#fff' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <h3 style={{ margin: 0 }}><CustomerLink name={behavior?.customerName} code={selectedCustomerId} /></h3>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#6B6B6B' }}
                                onClick={closeDetail}
                            >
                                ปิด ✕
                            </button>
                        </div>
                        {loadingBehavior && <p>กำลังโหลด...</p>}
                        {!loadingBehavior && behavior && (
                            <>
                                <div className="btc-summary-card" style={{ marginBottom: 12 }}>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">ถุงรวมทั้งช่วง</span>
                                        <span className="btc-summary-value">{behavior.totalQuantity}</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">ยอดรวม</span>
                                        <span className="btc-summary-value">{Number(behavior.totalPrice).toLocaleString()} ฿</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">จำนวนวันที่ซื้อ</span>
                                        <span className="btc-summary-value">{behavior.distinctPurchaseDays}</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">ถังที่ร้าน (ปัจจุบัน)</span>
                                        <span className="btc-summary-value">{tankCountOf(tankCounts, selectedCustomerId) ?? '—'}</span>
                                    </div>
                                </div>
                                <DailyBarChart days={behavior.days || []} />
                                <table className="btc-table" style={{ width: '100%', fontSize: 13 }}>
                                    <thead>
                                        <tr>
                                            <th>วันที่</th>
                                            <th>วัน</th>
                                            <th>จำนวนถุง</th>
                                            <th>ยอด (฿)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(behavior.days || []).map(d => (
                                            <tr key={d.date} style={{ background: d.quantity > 0 ? '#fff8f8' : undefined }}>
                                                <td>{d.date}</td>
                                                <td>{d.dayOfWeek}</td>
                                                <td style={{ fontWeight: d.quantity > 0 ? 600 : undefined }}>{d.quantity}</td>
                                                <td>{Number(d.total).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerPurchaseBehavior;
